import type {
	BoundaryData,
	HydrationOutcomeStats,
} from '@jira/platform__hydration-preservation-tracker/src/preservationTracker.tsx';
import {
	getReactFiberForElement,
	type ReactFiberKnownProperties,
} from '@jira/platform__react-fiber/src/reactFiber.tsx';

// React Fiber tag for SuspenseComponent.
const SUSPENSE_FIBER_TAG = 13;

/**
 * Walk fiber parents from the element under `node` until the first Suspense fiber.
 * Returns null if none. The result is only meaningful relative to a starting point.
 */
const findEnclosingSuspenseFiber = (node: Node): ReactFiberKnownProperties | null => {
	const el = node instanceof Element ? node : node.parentElement;
	if (!el) return null;
	let fiber = getReactFiberForElement(el);
	while (fiber) {
		if (fiber.tag === SUSPENSE_FIBER_TAG) return fiber;
		fiber = fiber.return;
	}
	return null;
};

/**
 * A child of a parent at diff time can be:
 *  - 'node'        — a direct child to compare structurally
 *  - 'placeholder' — content owned by a nested Suspense boundary; used only for structural matching.
 *                    `first` stays null because a placeholder represents a collapsed subtree.
 */
type ChildSlot = { kind: 'node'; first: Node } | { kind: 'placeholder'; first: null };

const PLACEHOLDER: ChildSlot = { kind: 'placeholder', first: null };

/**
 * When SSR opens with a Suspense placeholder, React inserts a zero-width text node on the
 * client side. Drop those leading text slots so the two lists align by slot index.
 */
const alignLeadingPlaceholder = (ssr: ChildSlot[], csr: ChildSlot[]): void => {
	if (ssr[0]?.kind !== 'placeholder') return;
	while (csr[0]?.kind === 'node' && csr[0].first.nodeType === Node.TEXT_NODE) {
		csr.shift();
	}
};

/**
 * SSR-side: build child slots from the captured (detached) DOM.
 * Nested Suspense regions are still bracketed by `$` / `$!` / `/$` comment markers and are
 * collapsed into a single placeholder slot.
 */
const getSsrChildSlots = (node: Node): ChildSlot[] => {
	const slots: ChildSlot[] = [];
	let suspenseDepth = 0;
	// Track whether the current top-level Suspense region had any real DOM nodes between
	// its `$…/$` markers. An empty Suspense boundary (renders nothing) leaves no live DOM
	// on the CSR side, so we must skip the placeholder slot to keep SSR/CSR aligned.
	let regionHadContent = false;
	for (const child of Array.from(node.childNodes)) {
		if (child.nodeType === Node.COMMENT_NODE) {
			const v = child.nodeValue;
			if (v === '$' || v === '$!') {
				if (suspenseDepth === 0) regionHadContent = false;
				suspenseDepth += 1;
			} else if (v === '/$' && --suspenseDepth === 0 && regionHadContent) {
				slots.push(PLACEHOLDER);
			}
			// eslint-disable-next-line no-continue
			continue;
		}
		if (suspenseDepth === 0) slots.push({ kind: 'node', first: child });
		else regionHadContent = true;
	}
	return slots;
};

/**
 * CSR-side: build child slots from the live DOM.
 * After successful rehydration there are no `$` / `/$` markers around healed nested Suspense
 * subtrees, so we use React fiber ownership: any child under a different nearest Suspense
 * boundary is folded into a single placeholder slot.
 */
const getCsrChildSlots = (node: Node): ChildSlot[] => {
	const parentBoundary = findEnclosingSuspenseFiber(node);
	const slots: ChildSlot[] = [];
	// Track the boundary fiber of the previous slot so we only collapse adjacent children
	// belonging to the SAME nested Suspense boundary. Sibling boundaries each emit their
	// own placeholder to stay aligned with the SSR `$…/$` markers.
	let prevNestedBoundary: ReactFiberKnownProperties | null = null;
	for (const child of Array.from(node.childNodes)) {
		if (child.nodeType === Node.COMMENT_NODE) {
			// eslint-disable-next-line no-continue
			continue;
		}
		const childBoundary = findEnclosingSuspenseFiber(child);
		const isNested = childBoundary !== null && childBoundary !== parentBoundary;
		if (isNested && childBoundary === prevNestedBoundary) {
			// eslint-disable-next-line no-continue
			continue;
		}
		slots.push(isNested ? PLACEHOLDER : { kind: 'node', first: child });
		prevNestedBoundary = isNested ? childBoundary : null;
	}
	return slots;
};

/**
 * Filter children for slot-index alignment against the pre-hydration snapshot.
 * Must exclude text nodes (unlike getFilteredChildren) because the slot index
 * captured in preservationTracker counts only element children — text nodes
 * shift indices and would misalign the boundary content window.
 */
const getSlotFilteredChildren = (node: Node): Node[] =>
	Array.from(node.childNodes).filter(
		(child) =>
			child.nodeType !== Node.COMMENT_NODE &&
			child.nodeType !== Node.TEXT_NODE &&
			!(child instanceof HTMLTemplateElement),
	);

/**
 * First diverging pair found while walking SSR and CSR trees in parallel.
 * `liveClientParent` is the deepest live CSR parent we recursed through — it always
 * points at a real, attached element with a fiber, even when `client` itself is null
 * (e.g. when an SSR node has no client counterpart at the slot).
 */
export type Mismatch = {
	server: Node | null;
	client: Node | null;
	liveClientParent: Node;
};

/**
 * Walk original (detached) and current (live) node trees in parallel.
 * Returns the first divergence with a live anchor for fiber-based location lookup.
 */
export const findFirstMismatch = (orig: Node, curr: Node): Mismatch | null => {
	if (
		orig.nodeType !== curr.nodeType ||
		orig.nodeName !== curr.nodeName ||
		(orig.nodeType === Node.TEXT_NODE && orig.textContent !== curr.textContent)
	) {
		return { server: orig, client: curr, liveClientParent: curr.parentNode ?? curr };
	}

	const origSlots = getSsrChildSlots(orig);
	const currSlots = getCsrChildSlots(curr);
	alignLeadingPlaceholder(origSlots, currSlots);
	let serverSlotIndex = 0;
	let clientSlotIndex = 0;

	while (serverSlotIndex < origSlots.length || clientSlotIndex < currSlots.length) {
		const origSlot = origSlots[serverSlotIndex];
		const currSlot = currSlots[clientSlotIndex];
		if (!origSlot) {
			return { server: null, client: currSlot?.first ?? null, liveClientParent: curr };
		}
		if (!currSlot) {
			return { server: origSlot.first ?? null, client: null, liveClientParent: curr };
		}
		if (origSlot.kind === 'placeholder' || currSlot.kind === 'placeholder') {
			if (origSlot.kind !== currSlot.kind) {
				return { server: origSlot.first, client: currSlot.first, liveClientParent: curr };
			}
			serverSlotIndex += 1;
			clientSlotIndex += 1;
			// eslint-disable-next-line no-continue
			continue;
		}
		const origKid = origSlot.first;
		const currKid = currSlot.first;
		if (!origKid) {
			return { server: null, client: currKid ?? null, liveClientParent: curr };
		}
		if (!currKid) {
			return { server: origKid, client: null, liveClientParent: curr };
		}
		const inner = findFirstMismatch(origKid, currKid);
		if (inner) {
			// Inner frame already set the deepest live parent; keep it.
			return inner;
		}
		serverSlotIndex += 1;
		clientSlotIndex += 1;
	}

	return null;
};

/**
 * Returns the outermost failed boundary markers — those not nested inside another failed marker.
 */
const getOutermostFailedMarkers = (boundaryData: BoundaryData): Node[] => {
	const { failedBoundaryMarkers, nestedMarkersByMarker } = boundaryData;
	const nested = new Set<Node>();
	for (const marker of failedBoundaryMarkers) {
		for (const child of nestedMarkersByMarker.get(marker) ?? []) {
			if (failedBoundaryMarkers.has(child)) {
				nested.add(child);
			}
		}
	}
	return [...failedBoundaryMarkers].filter((m) => !nested.has(m));
};

/** First structurally diverging pair across the failed boundary, with a live anchor for fiber tracing */
export type HydrationMismatchResult = {
	/** What kind of structural divergence was detected */
	kind: HydrationMismatchKind;
	/** The server-side (detached/dead) node at the point of divergence, or null if the client has an extra node */
	serverNode: Node | null;
	/** The client-side (live) node at the point of divergence, or null if the server has an extra node */
	clientNode: Node | null;
	liveAnchor: HTMLElement | null;
};

/** What kind of structural divergence was detected */
export type HydrationMismatchKind =
	/** Server and client both have a node here, but they differ (tag or text) */
	| 'difference'
	/** The server rendered a node that is absent on the client */
	| 'missing'
	/** The client rendered a node that was never on the server */
	| 'extra';

const toLiveAnchor = (node: Node | null, fallback: HTMLElement | null): HTMLElement | null =>
	node instanceof HTMLElement ? node : (node?.parentElement ?? fallback);

export const hydrationDiffer = (stats: HydrationOutcomeStats): HydrationMismatchResult | null => {
	const { boundaryData } = stats;
	if (!boundaryData) {
		return null;
	}
	const outermostMarkers = getOutermostFailedMarkers(boundaryData);
	if (outermostMarkers.length === 0) {
		return null;
	}

	// When multiple outermost boundaries share the same parent they are adjacent siblings —
	// slot reconstruction is ambiguous in that case, so bail out.
	// (When each boundary has a distinct parent we still only process the first one below.)
	if (outermostMarkers.length > 1) {
		const parents = new Set(
			outermostMarkers.map((m) => boundaryData.capturedParentByMarker.get(m)),
		);
		if (parents.size < outermostMarkers.length) {
			// two or more boundaries share the same parent — adjacent siblings, unsupported
			return null;
		}
	}

	const marker = outermostMarkers[0];
	const parent = boundaryData.capturedParentByMarker.get(marker) ?? null;
	if (!parent) {
		return null;
	}

	const origChildren = boundaryData.capturedTopLevelChildrenByMarker.get(marker) ?? [];
	if (origChildren.length === 0) {
		return null;
	}

	const slotIndex = boundaryData.capturedSlotIndexByMarker.get(marker) ?? -1;
	if (slotIndex < 0) {
		return null;
	}

	// Current filtered element children of the preserved parent
	const currentFilteredChildren = getSlotFilteredChildren(parent);
	// Slice the current children at the same slot range as the original boundary content.
	// Use all remaining current children from slotIndex so extra client-side nodes are visible.
	const currChildren = currentFilteredChildren.slice(slotIndex);

	const parentAnchor = parent instanceof HTMLElement ? parent : null;
	const len = Math.max(origChildren.length, currChildren.length);
	for (let i = 0; i < len; i++) {
		const orig = origChildren[i];
		const curr = currChildren[i];

		// Best fallback: the previous live sibling, or the parent as last resort
		const prevSibling = i > 0 ? currChildren[i - 1] : null;
		const bestFallback = toLiveAnchor(prevSibling ?? null, parentAnchor);

		if (!orig) {
			return {
				kind: 'extra',
				serverNode: null,
				clientNode: curr ?? null,
				liveAnchor: toLiveAnchor(curr ?? null, bestFallback),
			};
		}
		if (!curr) {
			return {
				kind: 'missing',
				serverNode: orig,
				clientNode: null,
				liveAnchor: bestFallback,
			};
		}

		const mismatch = findFirstMismatch(orig, curr);
		if (mismatch) {
			return {
				kind: 'difference',
				serverNode: mismatch.server,
				clientNode: mismatch.client,
				liveAnchor: toLiveAnchor(mismatch.client ?? mismatch.liveClientParent, bestFallback),
			};
		}
	}

	return null;
};
